import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, Sparkles, Zap, Rocket, Users, ArrowLeft } from 'lucide-react';
import { api } from '../lib/api.js';
import { useWorkspaces } from '../lib/queries.js';

interface PlanRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  priceBrl: number;
  trialDays: number | null;
  features: Record<string, any>;
  aiLimits: { monthlyTokenLimit?: number; burstLimit?: number; modules?: Record<string, unknown> };
}

interface SubscriptionInfo {
  workspace: { id: string; name: string; slug: string };
  subscription: { status: string; lockedPriceBrl: number; trialEndsAt: string | null; currentPeriodEnd: string | null } | null;
  planVersion: { priceBrl: number; features: any; aiLimits: any } | null;
  memberCount: number;
}

const PLAN_ICON: Record<string, React.ReactNode> = {
  free: <Sparkles size={20} />,
  starter: <Zap size={20} />,
  pro: <Rocket size={20} />,
  team: <Users size={20} />,
};

const BR = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function formatPrice(cents: number): string {
  return BR.format(cents / 100);
}

function featureBullets(p: PlanRow): string[] {
  const f = p.features ?? {};
  const ai = p.aiLimits ?? {};
  const out: string[] = [];
  if (typeof f.workspacesMax === 'number') out.push(`${f.workspacesMax} workspace${f.workspacesMax > 1 ? 's' : ''}`);
  if (typeof f.docsMax === 'number') out.push(`${f.docsMax === -1 ? 'Documentos ilimitados' : `${f.docsMax} documentos`}`);
  if (typeof f.seats === 'number') out.push(`${f.seats === -1 ? 'Assentos ilimitados' : `${f.seats} assento${f.seats > 1 ? 's' : ''}`}`);
  if (typeof f.assetStorageMb === 'number') out.push(`${f.assetStorageMb >= 1024 ? `${(f.assetStorageMb / 1024).toFixed(0)} GB` : `${f.assetStorageMb} MB`} de armazenamento`);
  if (f.collab) out.push('Colaboração em tempo real');
  if (f.share) out.push('Compartilhamento público');
  if (f.pdfExport) out.push('Exportar PDF');
  if (f.apiAccess) out.push('Acesso à API');
  if (typeof ai.monthlyTokenLimit === 'number' && ai.monthlyTokenLimit > 0) {
    out.push(`${ai.monthlyTokenLimit.toLocaleString('pt-BR')} tokens IA/mês`);
  }
  return out;
}

export function BillingPage() {
  const navigate = useNavigate();
  const { data: wsData } = useWorkspaces();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // Default workspace: first one user owns/admins.
  useEffect(() => {
    if (workspaceId || !wsData?.workspaces?.length) return;
    const owned = wsData.workspaces.find((w) => w.role === 'owner') ?? wsData.workspaces[0];
    if (owned) setWorkspaceId(owned.id);
  }, [wsData, workspaceId]);

  // Fetch plans (public).
  useEffect(() => {
    setLoading(true);
    api<{ plans: PlanRow[] }>('/billing/plans')
      .then((d) => {
        // Stable order: free → starter → pro → team → others
        const order = ['free', 'starter', 'pro', 'team'];
        const sorted = [...d.plans].sort((a, b) => {
          const ai = order.indexOf(a.slug);
          const bi = order.indexOf(b.slug);
          if (ai === -1 && bi === -1) return a.priceBrl - b.priceBrl;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
        setPlans(sorted);
      })
      .catch((e) => setErr(e?.message ?? 'load_failed'))
      .finally(() => setLoading(false));
  }, []);

  // Fetch current subscription whenever workspace changes.
  useEffect(() => {
    if (!workspaceId) return;
    api<SubscriptionInfo>(`/billing/portal-data?workspaceId=${workspaceId}`)
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [workspaceId]);

  const currentPriceBrl = info?.subscription?.lockedPriceBrl ?? 0;
  const currentPlanSlug = useMemo(() => {
    if (!info?.planVersion || !plans.length) return null;
    return plans.find((p) => p.priceBrl === info.planVersion!.priceBrl)?.slug ?? null;
  }, [info, plans]);

  async function subscribe(p: PlanRow) {
    if (!workspaceId) {
      setActionErr('Selecione um workspace.');
      return;
    }
    if (p.slug === 'free') return;
    setActionErr(null);
    setBusySlug(p.slug);
    try {
      const res = await api<{ checkoutUrl: string }>('/billing/checkout', {
        method: 'POST',
        body: {
          workspaceId,
          planSlug: p.slug,
          backUrl: window.location.origin + '/billing',
        },
      });
      window.location.href = res.checkoutUrl;
    } catch (e: any) {
      const code = e?.body?.error ?? e?.message ?? 'erro';
      setActionErr(`Falha ao iniciar checkout: ${code}`);
    } finally {
      setBusySlug(null);
    }
  }

  return (
    <main style={shellStyle}>
      <div style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '32px 24px 80px' }}>
        <header style={headerStyle}>
          <div>
            <button onClick={() => navigate({ to: '/' })} style={backBtnStyle}>
              <ArrowLeft size={14} /> Voltar
            </button>
            <h1 style={titleStyle}>Planos & Assinatura</h1>
            <p style={subtitleStyle}>
              Escolha o plano ideal para seu workspace. Pagamento via Mercado Pago, cancelamento a qualquer momento.
            </p>
          </div>
          {wsData?.workspaces && wsData.workspaces.length > 0 && (
            <div style={wsSelectorStyle}>
              <label style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Workspace
              </label>
              <select
                value={workspaceId ?? ''}
                onChange={(e) => setWorkspaceId(e.target.value)}
                style={selectStyle}
              >
                {wsData.workspaces.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          )}
        </header>

        {/* Current status banner */}
        {info?.subscription && (
          <div style={statusBannerStyle}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Plano atual</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginTop: 4 }}>
                {currentPlanSlug ? plans.find((p) => p.slug === currentPlanSlug)?.name ?? 'Custom' : 'Custom'}
                <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>
                  {formatPrice(currentPriceBrl)}/mês
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={statusPillStyle(info.subscription.status)}>{info.subscription.status}</span>
              {info.subscription.trialEndsAt && (
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  Trial até {new Date(info.subscription.trialEndsAt).toLocaleDateString('pt-BR')}
                </span>
              )}
            </div>
          </div>
        )}

        {actionErr && (
          <div style={errorBoxStyle}>{actionErr}</div>
        )}

        {/* Plan grid */}
        {loading ? (
          <div style={{ color: 'var(--muted)', padding: 32, textAlign: 'center' }}>Carregando planos…</div>
        ) : err ? (
          <div style={{ color: '#fca5a5', padding: 32 }}>Erro: {err}</div>
        ) : (
          <div style={gridStyle}>
            {plans.map((p) => {
              const isCurrent = p.slug === currentPlanSlug;
              const isHighlight = p.slug === 'pro';
              const bullets = featureBullets(p);
              const free = p.priceBrl === 0;
              return (
                <article
                  key={p.id}
                  style={planCardStyle(isHighlight, isCurrent)}
                >
                  {isHighlight && (
                    <div style={highlightBadgeStyle}>Recomendado</div>
                  )}
                  <div style={planIconStyle(isHighlight)}>{PLAN_ICON[p.slug] ?? <Sparkles size={20} />}</div>
                  <h2 style={planNameStyle}>{p.name}</h2>
                  {p.description && <p style={planDescStyle}>{p.description}</p>}
                  <div style={priceRowStyle}>
                    <span style={priceStyle}>
                      {free ? 'Grátis' : formatPrice(p.priceBrl)}
                    </span>
                    {!free && <span style={priceUnitStyle}>/mês</span>}
                  </div>
                  {p.trialDays != null && p.trialDays > 0 && !free && (
                    <div style={trialPillStyle}>{p.trialDays} dias de trial</div>
                  )}
                  <ul style={bulletListStyle}>
                    {bullets.map((b, i) => (
                      <li key={i} style={bulletItemStyle}>
                        <Check size={13} style={{ color: 'var(--neon)', flexShrink: 0, marginTop: 2 }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => subscribe(p)}
                    disabled={free || isCurrent || busySlug === p.slug || !workspaceId}
                    style={ctaBtnStyle({ isHighlight, isCurrent, disabled: free || isCurrent || !workspaceId, busy: busySlug === p.slug })}
                  >
                    {isCurrent
                      ? 'Plano atual'
                      : free
                      ? 'Plano gratuito'
                      : busySlug === p.slug
                      ? 'Abrindo checkout…'
                      : 'Assinar'}
                  </button>
                </article>
              );
            })}
          </div>
        )}

        <p style={footnoteStyle}>
          Cobrança recorrente em BRL via Mercado Pago. Você pode cancelar a qualquer momento; o acesso permanece ativo até o fim do ciclo pago.
        </p>
      </div>
    </main>
  );
}

const shellStyle: React.CSSProperties = {
  minHeight: '100dvh',
  background: 'radial-gradient(circle at 30% 0%, rgba(var(--neon-rgb), 0.08), transparent 50%), var(--canvas-bg)',
  color: 'var(--text)',
  fontFamily: 'var(--font-display)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 28,
};

const backBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '6px 11px',
  color: 'var(--text-dim, var(--text))',
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginBottom: 14,
};

const titleStyle: React.CSSProperties = {
  fontSize: 30,
  fontWeight: 700,
  letterSpacing: '-0.015em',
  margin: 0,
  background: 'linear-gradient(135deg, var(--neon), var(--violet))',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--muted)',
  margin: '6px 0 0',
  maxWidth: 540,
  lineHeight: 1.55,
};

const wsSelectorStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 5,
  minWidth: 200,
};

const selectStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '7px 10px',
  color: 'var(--text)',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
};

const statusBannerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 12,
  padding: '14px 18px',
  background: 'linear-gradient(180deg, rgba(var(--neon-rgb), 0.10), rgba(var(--neon-rgb), 0.03))',
  border: '1px solid rgba(var(--neon-rgb), 0.18)',
  borderRadius: 12,
  marginBottom: 28,
};

function statusPillStyle(status: string): React.CSSProperties {
  const tone =
    status === 'active' ? { bg: 'rgba(74, 222, 128, 0.14)', border: 'rgba(74, 222, 128, 0.4)', color: '#4ade80' }
    : status === 'trialing' ? { bg: 'rgba(0, 245, 255, 0.14)', border: 'rgba(0, 245, 255, 0.4)', color: 'var(--neon)' }
    : { bg: 'rgba(248, 113, 113, 0.12)', border: 'rgba(248, 113, 113, 0.35)', color: '#fca5a5' };
  return {
    fontSize: 11,
    padding: '4px 10px',
    borderRadius: 999,
    background: tone.bg,
    border: `1px solid ${tone.border}`,
    color: tone.color,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontWeight: 600,
  };
}

const errorBoxStyle: React.CSSProperties = {
  marginBottom: 16,
  padding: '10px 14px',
  borderRadius: 10,
  background: 'rgba(248, 113, 113, 0.10)',
  border: '1px solid rgba(248, 113, 113, 0.32)',
  color: '#fca5a5',
  fontSize: 12.5,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

function planCardStyle(highlight: boolean, current: boolean): React.CSSProperties {
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 22px 22px',
    background: highlight
      ? 'linear-gradient(180deg, rgba(var(--neon-rgb), 0.08), rgba(var(--neon-rgb), 0.02))'
      : 'rgba(255,255,255,0.025)',
    border: `1px solid ${current ? 'var(--neon)' : highlight ? 'rgba(var(--neon-rgb), 0.45)' : 'var(--border)'}`,
    borderRadius: 16,
    boxShadow: highlight
      ? '0 12px 36px rgba(0,0,0,0.4), 0 0 28px rgba(var(--neon-rgb), 0.12)'
      : '0 4px 18px rgba(0,0,0,0.3)',
    transition: 'transform .2s var(--ease-out)',
  };
}

const highlightBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: -10,
  right: 16,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  padding: '4px 10px',
  borderRadius: 999,
  background: 'var(--neon)',
  color: '#02131a',
  boxShadow: '0 0 14px rgba(var(--neon-rgb), 0.5)',
};

function planIconStyle(highlight: boolean): React.CSSProperties {
  return {
    width: 40, height: 40,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 10,
    background: highlight ? 'rgba(var(--neon-rgb), 0.16)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${highlight ? 'rgba(var(--neon-rgb), 0.36)' : 'var(--border)'}`,
    color: highlight ? 'var(--neon)' : 'var(--text)',
    marginBottom: 14,
  };
}

const planNameStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: '-0.008em',
  margin: 0,
};

const planDescStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--muted)',
  margin: '4px 0 0',
  lineHeight: 1.5,
};

const priceRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  margin: '18px 0 4px',
};

const priceStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  letterSpacing: '-0.015em',
  color: 'var(--text)',
};

const priceUnitStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--muted)',
};

const trialPillStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  fontSize: 10,
  padding: '3px 8px',
  borderRadius: 999,
  background: 'rgba(var(--neon-rgb), 0.10)',
  border: '1px solid rgba(var(--neon-rgb), 0.30)',
  color: 'var(--neon)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 600,
  marginBottom: 16,
};

const bulletListStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: '14px 0 22px',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  flex: 1,
};

const bulletItemStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  fontSize: 12.5,
  lineHeight: 1.45,
  color: 'var(--text-dim, var(--text))',
};

function ctaBtnStyle(o: { isHighlight: boolean; isCurrent: boolean; disabled: boolean; busy: boolean }): React.CSSProperties {
  const base: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    fontSize: 13,
    fontWeight: 700,
    borderRadius: 10,
    cursor: o.disabled ? 'default' : 'pointer',
    transition: 'transform .12s, box-shadow .12s',
    fontFamily: 'inherit',
    border: 'none',
  };
  if (o.isCurrent) {
    return { ...base,
      background: 'transparent',
      color: 'var(--muted)',
      border: '1px solid var(--border)',
      cursor: 'default',
    };
  }
  if (o.disabled && !o.busy) {
    return { ...base,
      background: 'rgba(255,255,255,0.04)',
      color: 'var(--muted)',
      border: '1px solid var(--border)',
    };
  }
  if (o.isHighlight) {
    return { ...base,
      background: 'var(--neon)',
      color: '#02131a',
      boxShadow: '0 0 18px rgba(var(--neon-rgb), 0.35)',
    };
  }
  return { ...base,
    background: 'rgba(var(--neon-rgb), 0.12)',
    color: 'var(--neon)',
    border: '1px solid rgba(var(--neon-rgb), 0.36)',
  };
}

const footnoteStyle: React.CSSProperties = {
  marginTop: 32,
  fontSize: 11,
  color: 'var(--muted)',
  textAlign: 'center',
  lineHeight: 1.55,
};
